import React, { memo, useCallback, useState } from 'react';

import isValid from 'date-fns/isValid';
import lastDayOfMonth from 'date-fns/lastDayOfMonth';
import parseISO from 'date-fns/parseISO';
import isEqual from 'lodash/isEqual';

import AkCalendar from '@atlaskit/calendar';
import { formatIsoLocalDate } from '@atlassian/townsquare-date-utils/date-format';

import { useDateLocale } from '../../../common/utils/utils';
import type { CalendarProps } from '../../../types';

const getDateObj = (date: Date) => ({
	day: date.getDate(),
	month: date.getMonth() + 1,
	year: date.getFullYear(),
});

const getValidDate = (iso?: string) => {
	if (!iso) {
		return {};
	}

	const date: Date = parseISO(iso);

	return isValid(date) ? getDateObj(date) : {};
};

export const padToTwo = (num: number): string => (num <= 99 ? `0${num}`.slice(-2) : `${num}`);

export const DayPicker: React.MemoExoticComponent<(props: CalendarProps) => React.JSX.Element> =
	memo(
		(props: CalendarProps): React.JSX.Element => {
			const currentDate = props.date;
			const { minDate, maxDate } = props;
			const [viewDate, setViewDate] = useState(currentDate);
			const locale = useDateLocale();

			const onSelect = useCallback(
				(iso: string) => {
					setViewDate(iso);
					const newDateObj = {
						start: iso,
						end: iso,
					};
					props.onSelect(newDateObj);
				},
				[props],
			);

			const onChange = useCallback((iso?: string) => {
				if (!iso) {
					return;
				}

				const [year, month, date] = iso.split('-');
				let newIso = iso;

				const parsedDate: number = parseInt(date, 10);
				const parsedMonth: number = parseInt(month, 10);
				const parsedYear: number = parseInt(year, 10);
				const lastDayInMonth: number = lastDayOfMonth(
					new Date(
						parsedYear,
						parsedMonth - 1, // This needs to be -1, because the Date constructor expects an index of the given month
					),
				).getDate();

				if (lastDayInMonth < parsedDate) {
					newIso = `${year}-${padToTwo(parsedMonth)}-${padToTwo(lastDayInMonth)}`;
				} else {
					newIso = `${year}-${padToTwo(parsedMonth)}-${padToTwo(parsedDate)}`;
				}
				setViewDate(newIso);
			}, []);

			return (
				<AkCalendar
					{...getValidDate(viewDate)}
					minDate={minDate ? formatIsoLocalDate(minDate, locale) : undefined}
					maxDate={maxDate ? formatIsoLocalDate(maxDate, locale) : undefined}
					selected={[currentDate || '']}
					previouslySelected={[]}
					onChange={(e) => onChange(e.iso)}
					onSelect={(e) => onSelect(e.iso)}
				/>
			);
		},
		(prevProps: CalendarProps, nextProps: CalendarProps) => isEqual(prevProps.date, nextProps.date),
	);
